import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {HttpUtilsService, QueryParamsModel, QueryResultsModel} from '../_base/crud';
import {ConversationModel} from '../models/conversation.model';
import {map} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { OKResponse } from '../_base/crud/models/query-models/query-results.model';
import { isDirectChatStatus, IsReadStatus } from '../reducers/is_read.reducers';
import { ProjectModel } from '../models/project.model';
import { UserModel } from '../auth';
import { MessageModel } from '../models/message.model';
import queryString from 'query-string';
import { PushNotificationService } from './push-notification.service';

const API_CONVERSATIONS_URL = `${environment.pathToAPI}/api/v1/conversations`;

export type SystemMessage = 'CREATE_PROJECT'
        | 'CONFIRM_ESTIMATE'
        | 'REJECT_ESTIMATE'
        | 'CONFIRM_FINAL_VERSION'
        | 'REJECT_FINAL_VERSION'
        | 'SEND_SCHEDULE'
        | 'ADD_ANNOTATION'
// ここからSUUMO
        | 'UPLOAD_FILE'
        | 'UPLOAD_ADDITIONAL_FILE'
        | 'SUBMIT_ANGLES'
        | 'SELECT_ANGLE'
        | 'SUBMIT_DRAFT'
        | 'COPY_DRAFT'
        | 'SUBMIT_SECOND_DRAFT'
        | 'COPY_SECOND_DRAFT'
        | 'SUBMIT_THIRD_DRAFT'
        | 'COPY_THIRD_DRAFT'
        | 'SAVE_CHECK_BACK'
        | 'SUBMIT_CHECK_BACK'
        | 'DELIVERY_OK';

@Injectable()
export class ConversationsService {
    constructor(
        private http: HttpClient,
        private httpUtils: HttpUtilsService,
        private pushNotificationService: PushNotificationService,
        private translate: TranslateService) {
    }

    createConversation(conversation: ConversationModel): Observable<ConversationModel> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        return this.http.post<ConversationModel>(
            API_CONVERSATIONS_URL,
            conversation,
            {headers: httpHeaders});
    }

    getConversationById(conversationId: number): Observable<ConversationModel> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        return this.http.get<ConversationModel>(
            API_CONVERSATIONS_URL + `/${conversationId}`,
            {headers: httpHeaders});
    }

    findConversations(queryParams: QueryParamsModel<ConversationModel>): Observable<QueryResultsModel<ConversationModel>> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        return this.http.post(
            API_CONVERSATIONS_URL + '/find',
            queryParams,
            {headers: httpHeaders}).pipe(map<any, QueryResultsModel<ConversationModel>>((value) => {
            return new QueryResultsModel(value.items, value.totalCount, value.isReadStatus, '');
        }));
    }

    getMessagesOfConversation(conversationId: number, offset: number, limit: number): Observable<QueryResultsModel<MessageModel>> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        const query = queryString.stringify({
            offset,
            limit,
        });
        return this.http.get(
            `${API_CONVERSATIONS_URL}/${conversationId}/messages?${query}`,
            {headers: httpHeaders}).pipe(map<any, QueryResultsModel<MessageModel>>((response) => {
                return new QueryResultsModel(response.items, response.totalCount, )
            }));
    }

    
    getMessagesOfConversationAfter(conversationId: number, afterId: number): Observable<QueryResultsModel<MessageModel>> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        const query = queryString.stringify({
            "after_id": afterId
        });
        return this.http.get(
            `${API_CONVERSATIONS_URL}/${conversationId}/messages?${query}`,
            {headers: httpHeaders}).pipe(map<any, QueryResultsModel<MessageModel>>((response) => {
                return new QueryResultsModel(response.items, response.totalCount, )
            }));
    }

    postMessageOfConversation(conversationId: number, message: MessageModel, mobileNotificationData: any, token: string | null = null) {
        const httpHeaders = this.httpUtils.getHTTPHeaders(token);
        return this.http.post<MessageModel>(`${API_CONVERSATIONS_URL}/${conversationId}/messages`, message, {headers: httpHeaders}).pipe(map((value) => {
            if (mobileNotificationData) {
                this.pushNotificationService.sendPushNotificationToBackEnd(mobileNotificationData).subscribe(res => {
                    // console.log(res);
                });
            }
            return value;
        }));
    }

    updateConversation(conversation: ConversationModel): Observable<ConversationModel> {
        const httpHeader = this.httpUtils.getHTTPHeaders();
        return this.http.put<ConversationModel>(API_CONVERSATIONS_URL + `/${conversation.id}`, conversation, {headers: httpHeader});
    }

    deleteConversation(conversationId: number): Observable<OKResponse> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        const url = `${API_CONVERSATIONS_URL}/${conversationId}`;
        return this.http.delete<OKResponse>(url, {headers: httpHeaders});
    }

    addUsersToConversation(conversation: ConversationModel, userIds: number[], isReadOnly: number, removeUserIds: number[]): Observable<ConversationModel> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        const body = {userIds, isReadOnly, removeUserIds};
        const url = `${API_CONVERSATIONS_URL}/${conversation.id}/users`;
        return this.http.put<ConversationModel>(url, body, {headers: httpHeaders});
    }

    deleteUserFromConversation(conversation: ConversationModel, userId: number): Observable<ConversationModel> {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        const url = `${API_CONVERSATIONS_URL}/${conversation.id}/users/${userId}`;
        return this.http.delete<ConversationModel>(url, {headers: httpHeaders});
    }

    readConversation(conversationId: number, userId: number) {
        const httpHeaders = this.httpUtils.getHTTPHeaders();
        return this.http.get<{projectId: number; isRead: boolean}>(
            API_CONVERSATIONS_URL + `/${conversationId}/read/${userId}`,
            {headers: httpHeaders});
    }


     /**
     * 設定されたタイトル、または自動生成されたタイトルの翻訳、もしくは個人チャットの相手の名前
     * それ以外は、ユーザ名をつなげたもの。
     */
    resolveSubject(conversation: ConversationModel, currentUser?: UserModel): string {
        if (conversation.subject) {
            return conversation.subject;
        }
        if (conversation.subjectGeneratedFrom) {
            return this.translate.instant(conversation.subjectGeneratedFrom);
        }
        if (conversation.isPrivate) {
            for (const toUser of conversation.toUsers) {
                if (toUser.userInfo.id !== currentUser?.id) {
                    return toUser.userInfo.fullname + "(個人チャット)";
                }
            }
        }
        const subject: String[] = [];
        for (const toUser of conversation.toUsers) {
            subject.push(toUser.userInfo.fullname);
        }
        return subject.join(', ');
    }


    /**
     * 設定されたタイトル、または自動生成されたタイトルの翻訳、もしくは個人チャットの相手の名前
     * それ以外は、空文字。
     */
    resolveTitle(conversation?: ConversationModel, currentUser?: UserModel): string {
        if (conversation?.subject) {
            return conversation.subject;
        } else if (conversation?.subjectGeneratedFrom) {
            return this.translate.instant(conversation.subjectGeneratedFrom);
        } else if (conversation?.isPrivate) {
            for (const toUser of conversation.toUsers) {
                if (toUser.userInfo.id !== currentUser?.id) {
                    return toUser.userInfo.fullname + "(個人チャット)";
                }
            }
            return "";
        } else {
            return "";
        }
    }

    resolveReadStatus(item: ConversationModel, isReadStatus: IsReadStatus[]) {
        for (const status of isReadStatus) {
            if (isDirectChatStatus(status) && status.conversationId === item.id) {
                return status.isRead;
            }
        }
        return true;
    }

    resolveBodyId(conversation: ConversationModel) {
        if (conversation.isPrivate) {
            return 'DIRECT_MESSAGE_ADDED_BODY';
        }
        return 'MESSAGE_ADDED_BODY';
    }

    activeUsers(conversation: ConversationModel, project: ProjectModel | undefined) {
        if (!project) {
            return conversation.toUsers.map((toUser) => toUser.userInfo);
        }
        const projectUserIds = project.users.map(u => u.id);
        return conversation.toUsers.filter(u => {
            return projectUserIds.indexOf(u.userInfo.id) > -1;
        }).map((toUser) => toUser.userInfo);
    }

    translateSystemMessage(bodyForSystemMessage: SystemMessage): string {
        return this.translate.instant('CONVERSATIONS.SYSTEM_MESSAGE_' + bodyForSystemMessage) as string
    }
}
